/**
 * Settings — branching page. "My School" + "Billing" for
 * SCHOOL_ADMIN, "My Kitchen" for CHEF_ADMIN, plus the personal
 * "Account" panel (Security & Sign-In + Notifications) for every
 * persona — moved here from My Profile per PROD-27. My Profile
 * (avatar menu) keeps only identity.
 *
 * The Billing tab renders the same School AR panel as /billing/school.
 * The sidebar's Billing item is permission-gated in a way school
 * admins don't satisfy, so this tab is their way in — see
 * `settings/variants.ts`.
 *
 * v1 surface (Addendum F §5): grade-range editor inside My School.
 * Other school config (terms, rosters, menu preferences, documents,
 * compliance gates) is still placeholder.
 */

import { formatGradeRangeCodeLabel } from '@ordo/constants/grade-matcher'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { EmptyState } from '@ordo/ui/empty-state'
import { Skeleton } from '@ordo/ui/skeleton'
import { Switch } from '@ordo/ui/switch'
import { Building2, Calendar as CalendarIcon, School } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'

import { useOrderPreferencesQuery, useUpdateOrderPreferencesMutation } from '@/api/location-settings'
import { useHasSchoolOrg } from '@/api/school-onboarding'
import { type AdminSchool, useMySchoolAdminLocationsQuery } from '@/api/settings-grade-ranges'
import { BranchingSwitcher, type BranchingVariantOption } from '@/components/branching-switcher'
import { OrdoPageContainer } from '@/components/ordo-page-container'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import { SchoolSetupPendingEmptyState } from '@/components/school-setup-pending-empty-state'
import { AccountSection } from '@/components/settings/account-section'
import { AutoOrderExplainer } from '@/components/settings/auto-order-explainer'
import { CampusScope, CardTitleRow } from '@/components/settings/campus-scope'
import { DietaryNotesCard } from '@/components/settings/dietary-notes-card'
import { MilkSplitSettingsCard } from '@/components/settings/milk-split-settings-card'
import { PrefillSettingsCard } from '@/components/settings/prefill-settings-card'
import { SchoolAccountCard } from '@/components/settings/school-account-card'
import { StudentNameOrderCard } from '@/components/settings/student-name-order-card'
import { anyColumnOrdersByComponent, milkEditorSurface } from '@/lib/school-orders/columns'
import { acceptsSchoolOrders } from '@/lib/school-orders/orderable-campuses'
import { NO_SCHOOLS_EMPTY, SupportEmailButton } from '@/lib/support'
import { useProfile } from '@/stores/profile'

import { SchoolARPage } from './billing/school'
import { Placeholder } from './placeholder'
import { MyKitchenSection } from './settings/my-kitchen'
import {
  resolveSettings,
  settingsSwitcherSurface,
  type SettingsVariant,
  settingsVariantForPath,
} from './settings/variants'

export function SettingsDefaultRedirect(): React.ReactElement | null {
  const { profile, loading } = useProfile()
  if (loading || !profile) {
    return null
  }
  const variants = resolveSettings(profile.profileCodes, profile.isOrdoStaff)
  return <Navigate to={`/settings/${variants[0]!}`} replace />
}

export function SettingsPage() {
  const { profile } = useProfile()
  const { pathname } = useLocation()
  const urlVariant = settingsVariantForPath(pathname)
  const variants = useMemo(
    () => resolveSettings(profile?.profileCodes ?? [], profile?.isOrdoStaff ?? false),
    [profile?.profileCodes, profile?.isOrdoStaff],
  )

  if (urlVariant && !variants.includes(urlVariant)) {
    if (variants[0]) {
      return <Navigate to={`/settings/${variants[0]}`} replace />
    }
  }

  const show = (v: SettingsVariant): boolean => {
    if (urlVariant) {
      return urlVariant === v
    }
    return variants.includes(v)
  }

  const switcherVariants: BranchingVariantOption[] = variants.map((v) => ({
    urlFragment: v,
    key: v,
    surface: settingsSwitcherSurface(v),
  }))

  return (
    <OrdoPageContainer>
      <OrdoPageHeader icon={Building2} title="Settings" />
      <BranchingSwitcher kind="settings" variants={switcherVariants} />
      {profile?.isOrdoStaff && (
        <Button asChild variant="outline">
          <Link to="/settings/catalog/contains-review">Contains review and dish verification</Link>
        </Button>
      )}

      {show('my-school') ? <MySchoolSection /> : null}

      {show('billing') ? <SchoolARPage /> : null}

      {show('my-kitchen') ? <MyKitchenSection /> : null}

      {show('account') ? <AccountSection /> : null}
    </OrdoPageContainer>
  )
}

// ---------------------------------------------------------------------------
// My School
// ---------------------------------------------------------------------------

function MySchoolSection() {
  const schoolsQuery = useMySchoolAdminLocationsQuery()
  const schoolOrg = useHasSchoolOrg()
  const schools = useMemo<AdminSchool[]>(() => schoolsQuery.data?.me?.schoolAdminLocations ?? [], [schoolsQuery.data])
  // The page reads as ORG-level (Sheila, 2026-08-24) — there is deliberately
  // NO page-level campus dropdown, and the page is NOT stacked per campus
  // (#2138, rejected). Every card below that writes PER-CAMPUS data instead
  // carries its own compact campus control in its header, via `CampusScope`:
  // "all org info, if campus by campus, can breakdown within each section".
  //
  // #2143 anchored those cards to `schools[0]` instead, which left every
  // non-first campus in a district unreachable and sent its writes to the
  // first campus — reported by schools as "OVS not working" and "I don't have
  // permission to edit it". Never anchor a per-campus card to `schools[0]`
  // again; wrap it in `CampusScope`.

  if (schoolsQuery.isPending && !schoolsQuery.data) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }
  if (schoolsQuery.error) {
    return <ErrorPanel message={schoolsQuery.error.message} />
  }
  if (schools.length === 0) {
    if (schoolOrg.hasSchoolOrg) {
      return <SchoolSetupPendingEmptyState surface="settings" schoolName={schoolOrg.schoolName} />
    }
    return (
      <EmptyState
        icon={<School className="h-12 w-12" />}
        title={NO_SCHOOLS_EMPTY.title}
        description={NO_SCHOOLS_EMPTY.description}
        action={<SupportEmailButton subject="No schools linked — Settings" />}
      />
    )
  }
  const orgSchool = schools[0]
  if (!orgSchool) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-medium">My School</h2>
        <Badge variant="secondary">{orgSchool.organization.name}</Badge>
      </div>

      {/* Menu-slot editor (components/settings/menu-slot-editor.tsx) is
          hidden for now — slot config keeps driving menus from the DB,
          but school admins don't self-serve edit it from this page. */}

      {/* Per-campus: the component split and the milk shares are LOCATION-tier
          rows. One scope for the pair — they are one section and exactly ONE
          of the two owns the milk editor. */}
      <CampusScope schools={schools} label="pre-fill and milk split">
        {({ school, scopeControl }) => <SplitAndMilkSection school={school} scopeControl={scopeControl} />}
      </CampusScope>

      {/* Per-campus: every row reads one campus's provisioning, and each
          "Request a change" opens a BlockingTask anchored to its locationId. */}
      <CampusScope schools={schools} label="school account">
        {({ school, scopeControl }) => <SchoolAccountCard school={school} scopeControl={scopeControl} />}
      </CampusScope>

      {/* Student import (roster CSV wizard) is deliberately NOT mounted
          here — it belongs to task/onboarding-driven moments, not the
          always-on settings hub. It lives at /settings/my-school/roster-import
          for those flows to deep-link. Same for the Devices (kiosk pairing)
          card at /settings/my-school/devices, kept off this page for now. */}

      {/* Per-campus: `autoOrderEnabled` is a LocationOrderPreference column. */}
      <CampusScope schools={schools} label="auto-orders">
        {({ school, scopeControl }) => <AutoOrdersCard school={school} scopeControl={scopeControl} />}
      </CampusScope>

      {/* Per-campus: the roster it searches is one campus's enrollment. */}
      <CampusScope schools={schools} label="student name order">
        {({ school, scopeControl }) => <StudentNameOrderCard school={school} scopeControl={scopeControl} />}
      </CampusScope>

      {/* Per-campus: accommodations are read and submitted per locationId. */}
      <CampusScope schools={schools} label="dietary accommodation notes">
        {({ school, scopeControl }) => <DietaryNotesCard school={school} scopeControl={scopeControl} />}
      </CampusScope>

      {/* "documents" is deliberately NOT in this summary any more — the
          dietary-note upload above IS the documents slot this placeholder
          used to promise. */}
      <Placeholder icon={Building2} title="More school settings" summary="Terms, menu preferences, compliance gates." />
    </div>
  )
}

/**
 * The component-split + milk pair for ONE campus.
 *
 * Its own component (not inlined in `MySchoolSection`) so both cards read the
 * campus the section is SCOPED to. Reading `schools[0]` was how the first
 * campus's state decided which editor every district saw (#2143).
 */
function SplitAndMilkSection({ school, scopeControl }: { school: AdminSchool; scopeControl: ReactNode }) {
  const ordersByComponent = anyColumnOrdersByComponent(school)
  // Exactly ONE milk-split surface per campus (`milkEditorSurface`). A
  // by-component (OVS) campus gets the Pre-fill card, whose per-mealtime split
  // dialog already nests the milk-share rows — reachable unconditionally since
  // splits are always on for OVS. Every other campus gets the compact
  // Milk-split card, because milk derives per meal regardless of the split and
  // must stay editable.
  const milkSurface = milkEditorSurface({ ordersByComponent })

  return (
    <>
      {ordersByComponent ? <PrefillSettingsCard school={school} scopeControl={scopeControl} /> : null}
      {/* When BOTH render (a by-component campus with the split off) the
          Pre-fill card above already carries the section's picker — one
          picker per section, not one per card. */}
      {milkSurface === 'MILK_CARD' ? (
        <MilkSplitSettingsCard school={school} scopeControl={ordersByComponent ? undefined : scopeControl} />
      ) : null}
    </>
  )
}

/**
 * The campus's auto-order switch — the ONE auto-order control on this page.
 *
 * Sheila, 2026-08-03: *"Remove this lower enable auto order duplicate
 * section"*. There were two: a Y/N toggle inside the pre-fill card and a
 * separate entry card with its own explainer and Manage button. They are now
 * one card.
 *
 * ⚠️ It lives HERE, not in `PrefillSettingsCard`, because that card only
 * renders for OVS campuses (`anyColumnOrdersByComponent`). Putting the only
 * auto-order control inside it would leave every Straight Serve campus with
 * no way to switch auto-order on at all.
 *
 * Turning it ON opens the wizard — enabling without saying what to order
 * would gate the campus on with nothing to place.
 */
function AutoOrdersCard({ school, scopeControl }: { school: AdminSchool; scopeControl?: ReactNode }) {
  const preferencesQuery = useOrderPreferencesQuery(school.id)
  const updatePreferences = useUpdateOrderPreferencesMutation()
  const navigate = useNavigate()
  const enabled = preferencesQuery.data?.autoOrderEnabled ?? false
  // A PARENT-only campus can't take school orders (FACTORY-553), so a school
  // auto-order there is refused by submit on every run. Turning one ON is
  // blocked; turning an existing one OFF stays possible.
  const schoolOrdering = acceptsSchoolOrders(school)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitleRow scopeControl={scopeControl}>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarIcon className="h-4 w-4" />
            Auto-orders
          </CardTitle>
        </CardTitleRow>
        <p className="text-sm text-muted-foreground">
          Order the same meals on a repeating schedule, placed for you each week.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium">Enable auto-order</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {schoolOrdering ? (
                <>You set a quantity per menu option; your campus&rsquo;s component split decides how each one ships.</>
              ) : (
                'Families order for their own students at this campus, so school auto-orders are off.'
              )}
            </p>
          </div>
          <Switch
            checked={enabled}
            disabled={preferencesQuery.isPending || updatePreferences.isPending || (!schoolOrdering && !enabled)}
            onCheckedChange={(checked) => {
              updatePreferences.mutate(
                { locationId: school.id, autoOrderEnabled: checked },
                // ?schoolId — the wizard falls back to `schools[0]`, so a
                // district switching campus here would otherwise build the
                // standing order for the wrong campus.
                checked
                  ? {
                      onSuccess: () =>
                        navigate(`/settings/my-school/auto-orders/new?schoolId=${encodeURIComponent(school.id)}`),
                    }
                  : undefined,
              )
            }}
            aria-label="Enable auto-order"
          />
        </div>

        {enabled ? (
          <>
            <AutoOrderExplainer compact />
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">Review or change what gets ordered.</p>
              <Button asChild size="sm" variant="outline">
                <Link to={`/settings/my-school/auto-orders?schoolId=${encodeURIComponent(school.id)}`}>
                  Manage auto-orders
                </Link>
              </Button>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

function formatGradeRangeRowLabel(row: AdminSchool['gradeRanges'][number]): string {
  const codeLabel = formatGradeRangeCodeLabel(row.gradeRange.code)
  const customName = row.customName?.trim()
  if (customName && customName.toLowerCase() !== row.gradeRange.name.trim().toLowerCase()) {
    return customName
  }
  return codeLabel ?? row.gradeRange.name
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      Couldn't load school settings. {message}
    </div>
  )
}
